import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Modal,
  Group,
  ActionIcon,
  Center,
  Loader,
  Stack,
  Text,
  ScrollArea,
  SimpleGrid,
  rem,
} from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import { IconPhoto, IconVideo, IconLayoutGrid } from '@tabler/icons-react';
import { useLazyFullImageQuery } from '@repo/services';
import { isVideoFileByName } from '@repo/utils';
import '@mantine/carousel/styles.css';

// ─── Types ───────────────────────────────────────────────────────────────────

interface BlobMediaItem {
  id: number;
  docName: string;
  thumbnailImage?: string;
}

/**
 * Props for the BlobMediaBrowser modal — accepts a property ID, list of media items, and carousel controls.
 */
export interface BlobMediaBrowserProps {
  propertyId: string;
  items: BlobMediaItem[];
  opened: boolean;
  startItemId?: number | null;
  onClose: () => void;
}

/**
 * Carousel / lightbox modal for Azure Blob property images.
 * Lazily fetches the full-resolution image for each slide via the
 * `Storage/GetPropertyImage/:propertyId/:id/full` RTK Query endpoint.
 *
 * Ported from prism-old's MediaBrowser and promoted to @repo/ui so
 * both the inline media tab and the media drawer can share it.
 */
export function BlobMediaBrowser({
  propertyId,
  items,
  opened,
  startItemId,
  onClose,
}: BlobMediaBrowserProps) {
  const [index, setIndex] = useState(0);
  const [showThumbs, setShowThumbs] = useState(false);
  const [fullImages, setFullImages] = useState<Record<number, string>>({});
  const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set());

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const carouselRef = useRef<any>(null);
  const [triggerGetFullImage] = useLazyFullImageQuery();

  const closeHandler = useCallback(() => onClose(), [onClose]);

  // Set initial index when opened or startItemId changes
  useEffect(() => {
    if (opened && startItemId != null) {
      const idx = items.findIndex((m) => m.id === startItemId);
      if (idx >= 0) {
        setIndex(idx);
        setTimeout(() => carouselRef.current?.slideTo?.(idx), 0);
      }
    }
  }, [opened, startItemId, items]);

  // Fetch full image for a given item
  const fetchFullImage = useCallback(
    async (item: BlobMediaItem) => {
      if (fullImages[item.id] || loadingIds.has(item.id)) return;
      setLoadingIds((prev) => new Set(prev).add(item.id));
      try {
        const result = await triggerGetFullImage({ propertyId, id: String(item.id) });
        if (result?.isSuccess && typeof result.data === 'string') {
          setFullImages((prev) => ({ ...prev, [item.id]: result.data as string }));
        }
      } catch (err) {
        console.error('Failed to load full image:', err);
      } finally {
        setLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      }
    },
    [propertyId, triggerGetFullImage, fullImages, loadingIds],
  );

  // Fetch current image when index changes
  useEffect(() => {
    if (!opened || items.length === 0) return;
    const current = items[index];
    if (current) void fetchFullImage(current);
  }, [opened, index, items, fetchFullImage]);

  const current = items[index];

  const goPrev = useCallback(() => {
    if (!items.length) return;
    const next = (index - 1 + items.length) % items.length;
    carouselRef.current?.slideTo?.(next);
    setIndex(next);
  }, [index, items.length]);

  const goNext = useCallback(() => {
    if (!items.length) return;
    const next = (index + 1) % items.length;
    carouselRef.current?.slideTo?.(next);
    setIndex(next);
  }, [index, items.length]);

  // Keyboard nav
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!opened) return;
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'Escape') closeHandler();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [opened, closeHandler, goPrev, goNext]);

  // ── Carousel slides ────────────────────────────────────────────────────────

  const renderCarousel = () => {
    if (!items.length) {
      return (
        <Center mih={200}>
          <Text c="dimmed">No media to preview</Text>
        </Center>
      );
    }

    return (
      <Carousel
        initialSlide={index}
        onSlideChange={setIndex}
        slideSize="100%"
        slideGap="md"
        controlsOffset="xs"
        loop
        align="center"
        ref={carouselRef}
      >
        {items.map((item) => {
          const isVideo = isVideoFileByName(item.docName);
          const fullUrl = fullImages[item.id];
          const isLoading = loadingIds.has(item.id);

          return (
            <Carousel.Slide key={item.id}>
              <Center style={{ maxHeight: '65vh', overflow: 'hidden' }}>
                {isLoading && !fullUrl ? (
                  <Stack align="center" gap="xs" py="xl">
                    <Loader />
                    <Text c="dimmed" size="sm">Loading…</Text>
                  </Stack>
                ) : isVideo ? (
                  <video
                    src={fullUrl || item.thumbnailImage}
                    controls
                    autoPlay
                    style={{
                      maxHeight: '65vh',
                      maxWidth: '100%',
                      width: 'auto',
                      borderRadius: 8,
                      background: '#000',
                      display: 'block',
                    }}
                  />
                ) : (
                  <img
                    src={fullUrl || item.thumbnailImage}
                    alt={item.docName}
                    style={{
                      maxHeight: '65vh',
                      maxWidth: '100%',
                      width: 'auto',
                      height: 'auto',
                      borderRadius: 8,
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                )}
              </Center>
            </Carousel.Slide>
          );
        })}
      </Carousel>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Modal.Root opened={opened} onClose={closeHandler} size="xl" centered>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>
            <Text size="xl">{current?.docName}</Text>
          </Modal.Title>
          <Group justify="flex-end" gap={4}>
            <ActionIcon
              variant="subtle"
              onClick={() => setShowThumbs((s) => !s)}
              aria-label="Toggle thumbnails"
            >
              <IconLayoutGrid />
            </ActionIcon>
            <Modal.CloseButton onClick={closeHandler} />
          </Group>
        </Modal.Header>
        <Modal.Body>
          <Stack gap="xs">
            {renderCarousel()}

            {/* Thumbnail strip */}
            {showThumbs && items.length > 1 && (
              <ScrollArea style={{ marginTop: rem(12), maxHeight: rem(120) }} type="auto">
                <SimpleGrid cols={Math.min(8, items.length)} spacing="xs">
                  {items.map((item, i) => {
                    const isVideo = isVideoFileByName(item.docName);
                    return (
                      <div
                        key={item.id}
                        style={{
                          cursor: 'pointer',
                          padding: 2,
                          border: i === index
                            ? '2px solid var(--mantine-color-blue-6)'
                            : '2px solid transparent',
                          borderRadius: 6,
                        }}
                        onClick={() => {
                          carouselRef.current?.slideTo?.(i);
                          setIndex(i);
                        }}
                      >
                        {item.thumbnailImage ? (
                          <img
                            src={item.thumbnailImage}
                            alt={item.docName}
                            style={{
                              width: '100%',
                              height: rem(64),
                              objectFit: 'cover',
                              borderRadius: 6,
                              display: 'block',
                            }}
                          />
                        ) : (
                          <Center
                            style={{
                              height: rem(64),
                              border: '1px solid var(--mantine-color-gray-4)',
                              borderRadius: 6,
                            }}
                          >
                            {isVideo ? <IconVideo size={24} /> : <IconPhoto size={24} />}
                          </Center>
                        )}
                      </div>
                    );
                  })}
                </SimpleGrid>
              </ScrollArea>
            )}

            {items.length > 1 && (
              <Text size="xs" ta="center" c="dimmed">
                {index + 1} / {items.length}
              </Text>
            )}
          </Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}

